/**
 * The pinned catalogue snapshot format (`data/<catalogVersion>/catalog.json`), written by
 * `tools/catalog-import` from Runebound's `public.set`, `public.card` and `public.printing` tables
 * (research 03 §1.3) and validated with zod when loaded (a trust boundary, docs/design/08 §1.1).
 */
import { z } from 'zod';

export const CATALOG_FORMAT = 1;

export const CARD_TYPES = ['Unit', 'Spell', 'Gear', 'Legend', 'Battlefield', 'Rune'] as const;
export type CardType = (typeof CARD_TYPES)[number];

export const SUPERTYPES = ['Champion', 'Signature', 'Token', 'Basic'] as const;
export type Supertype = (typeof SUPERTYPES)[number];

/** The six domains plus Colorless (domainless cards and every battlefield, research 01 §3.2). */
export const CATALOG_DOMAINS = ['Fury', 'Calm', 'Mind', 'Body', 'Chaos', 'Order', 'Colorless'] as const;
export type CatalogDomain = (typeof CATALOG_DOMAINS)[number];

export const SetSchema = z.strictObject({
  code: z.string().min(2).max(8),
  name: z.string().min(1),
  cardTotal: z.int().nonnegative(),
  printingCount: z.int().nonnegative(),
  releasedOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
});
export type CatalogSet = z.infer<typeof SetSchema>;

export const CardSchema = z.strictObject({
  /** Runebound `card.id` slug. */
  id: z.string().min(1).max(120),
  name: z.string().min(1),
  /** Card type; corrected when the catalogue is known to be wrong (see `typeOverride`). */
  type: z.enum(CARD_TYPES),
  supertype: z.enum(SUPERTYPES).nullable(),
  domains: z.array(z.enum(CATALOG_DOMAINS)),
  energy: z.int().nullable(),
  power: z.int().nullable(),
  might: z.int().nullable(),
  tags: z.array(z.string()),
  /** The catalogue's keyword column: incomplete, derive keywords from the text (research 03 §1.4). */
  keywords: z.array(z.string()),
  /** Rules text as Riftcodex HTML (`<p>`, `<br />`, `<ul><li>`); the best source for ability boundaries. */
  textRich: z.string().nullable(),
  /** Curated plain text: `textRich` (or `text_plain`) as lines, bullets as `• `; what `textHash` covers. */
  oracle: z.string(),
  /** `sha256:<hex>` of `oracle`; card ruleset entries record it to detect errata (docs/design/04 §6). */
  textHash: z.string().regex(/^sha256:[0-9a-f]{64}$/),
  flavour: z.string().nullable(),
  /** Runebound's `public.plain_printing(id)`: the art for a deck card that names no printing. */
  plainPrintingId: z.string().nullable(),
  /** For variant rows (Metal, Starter, Launch Exclusive, extra token arts): the base rules identity. */
  aliasOf: z.string().nullable(),
  /** Set when `type` overrides a known catalogue error (Radiance preview data, research 03 §1.5). */
  typeOverride: z.strictObject({ catalogType: z.enum(CARD_TYPES), reason: z.string() }).nullable(),
});
export type CatalogCard = z.infer<typeof CardSchema>;

export const PrintingSchema = z.strictObject({
  /** Lower-cased public code with dashes: `ogn-202a-298`, `ven-189*-166`, `sfd-t03`, `rad-r04a`. */
  id: z.string().min(3).max(40),
  cardId: z.string().min(1),
  setCode: z.string().min(2).max(8),
  number: z.int(),
  suffix: z.string(),
  publicCode: z.string(),
  rarity: z.string(),
  isAltArt: z.boolean(),
  isOvernumbered: z.boolean(),
  isSignature: z.boolean(),
  isPromo: z.boolean(),
  hasFoil: z.boolean(),
  /** Riot CDN URL (`cmsassets.rgpub.io/...-744x1039.png?accountingTag=RB`). */
  imageUrl: z.string(),
  artist: z.string().nullable(),
  /** Riot's alt text with the full rules text (accessibility, research 03 §1.3). */
  accessibilityText: z.string().nullable(),
});
export type CatalogPrinting = z.infer<typeof PrintingSchema>;

export const CatalogDataSchema = z.strictObject({
  format: z.literal(CATALOG_FORMAT),
  /** Runebound `catalog_version.version`, e.g. `20261008.1`. */
  version: z.string().min(1),
  /** Runebound `catalog_version.checksum`. */
  checksum: z.string(),
  /** Runebound `catalog_version.source` (`riftcodex`). */
  source: z.string(),
  /** When Runebound published this catalogue version (ISO 8601). */
  createdAt: z.string(),
  sets: z.array(SetSchema),
  cards: z.array(CardSchema),
  printings: z.array(PrintingSchema),
});
export type CatalogData = z.infer<typeof CatalogDataSchema>;
